import { FC } from 'react'
import { Button, Col, FlexboxGrid } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import PageNext from '@rsuite/icons/PageNext'
import Link from 'next/link'

import useUrls from '../services/useUrls'
import CheckIcon from './Icons/CheckIcon'
import { DIM_MID_COLOR } from '../utils/constants'
import useLoginModal from '../services/useLoginModal'
import DroppeLogo from './Icons/DroppeLogo'
import SafeTranslate from './common/SafeTranslate'
import { useAuth } from '../services/useAuth'

import buttonStyles from '../styles/CustomButtons.module.less'
import styles from '../styles/SourceToOrder.module.less'

interface CheckBulletProps {
  text: string
}

const SourceToOrder: FC = () => {
  const { urlT } = useUrls()
  const { t } = useTranslation('landing')
  const { openRegisterModal } = useLoginModal()
  const { user } = useAuth()
  const { pushT } = useUrls()

  const CheckBullet: FC<CheckBulletProps> = ({ text }) => (
    <FlexboxGrid className={styles['check-section']}>
      <div className="flex-shrink-col-static"><CheckIcon color={DIM_MID_COLOR} /></div>
      <FlexboxGrid.Item className="margin-left-spacer flex-grow-1 flex-shrink-col-dynamic">
        {t(text)}
      </FlexboxGrid.Item>
    </FlexboxGrid>
  )

  return (
    <div className={styles['source-to-order']}>
      <FlexboxGrid>
        <FlexboxGrid.Item
          as={Col}
          lg={8}
          xs={24}
          className={styles['source-section']}
        >
          <DroppeLogo size="md" />
          <h2 className="margin-top-spacer-double h1">{t('common:Autonomous sourcing platform')}</h2>
          <p className={styles.text}>
            <SafeTranslate
              i18nKey="common:The most efficient way to source and order supplies for your operations"
              components={{
                span: <span />,
              }}
            />
          </p>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          xl={9}
          lg={8}
          md={12}
          xs={24}
          className={styles.section}
        >
          <div>
            <h3 className="margin-top-spacer padding-top-spacer-half h5">
              {t('Sourcing')}
            </h3>
            <CheckBullet text="Find all possible options within Europe in one place" />
            <CheckBullet text="Tap into the purchasing power of 1500 customers" />
            <CheckBullet text="Set Price Alerts for your products and volumes" />
          </div>
          <Link href={urlT('/products')}>
            <FlexboxGrid align="middle">
              <FlexboxGrid.Item>
                <div className={styles['navigation-link']}>
                  <b>{t('Source products')}</b>
                  <span>
                    <PageNext
                      color={DIM_MID_COLOR}
                      height="15"
                    />
                  </span>
                </div>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Link>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          xl={7}
          lg={8}
          md={12}
          xs={24}
          className={styles.section}
        >
          <div>
            <h3 className="margin-top-spacer padding-top-spacer-half h5">
              {t('Ordering')}
            </h3>
            <CheckBullet text="common:Single process to order for your suppliers" />
            <CheckBullet text="Centralize orders to reduce admin work" />
            <CheckBullet text="Easily coordinate orders with your team" />
          </div>
          <Button
            onClick={() => (user ? pushT('/my-catalogs') : openRegisterModal())}
            appearance="link"
            className={`${buttonStyles['custom-button']} ${buttonStyles['button-tertiary']}`}
          >
            <div className={styles['navigation-link']}>
              <b>
                {t('Create your smart catalog')}
              </b>
              <span>
                <PageNext
                  color={DIM_MID_COLOR}
                  height="15"
                />
              </span>
            </div>
          </Button>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </div>
  )
}

export default SourceToOrder
